import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, UploadOutlined } from '@/shared/ui/icons';
import {
  Alert, Button, Card, Input, InputNumber, Select, Space, Switch, Tag, Divider,
} from '@/shared/ui/primitives';
import { UserSelect, CitySelect, DistrictSelect, NationalitySelect } from '@/shared/ui/entity-selects';
import PageHeader from '@/shared/components/PageHeader';
import { isNegotiablePrice, negotiableLabel } from '@/shared/utils/formatters';
import {
  aiAdApi,
  type AiAdExtraction,
  type AnalyzeResponse,
  type AssignmentMode,
  type CreateResponse,
  type DetectedType,
  type Gender,
  type Smoking,
} from '../api/aiAds.api';

const SAMPLES = [
  {
    label: 'عربي · يبحث عن سكن',
    text:
      'اسمي محمد، عمري 29، سعودي، موظف، غير مدخن، ابحث عن غرفة في شمال الرياض، ميزانيتي حوالي 1800 ريال. ' +
      'نظافة عالية، هادئ، أفضل روميت سعودي أو خليجي. للتواصل 0509905713 (واتساب).',
  },
  {
    label: 'عربي · عرض غرفة',
    text:
      'عندي غرفة خاصة في حي العليا بالرياض للإيجار الشهري 2000 ريال، مفروشة بالكامل، قريبة من المترو. ' +
      'يفضل موظف غير مدخن. للتواصل واتساب 0537222386.',
  },
  {
    label: 'English · looking for a room',
    text:
      'Hi, I am Sarah, 26, female, looking for a private room in Jeddah near Al Hamra. Budget up to 2200 SAR. ' +
      "I'm a non-smoker, employed, clean and quiet. WhatsApp me on 0545882776.",
  },
  {
    label: 'English · room for rent',
    text:
      'I have a private room for rent in Olaya, Riyadh. Monthly 1800 SAR, fully furnished, close to metro. ' +
      'Prefer employed non-smoker male. Call or WhatsApp 0504712871.',
  },
];

function toneForConfidence(c: number | null | undefined) {
  if (c == null) return 'default';
  if (c >= 0.8) return 'success';
  if (c >= 0.55) return 'warning';
  return 'error';
}

export default function AiAdCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [text, setText] = useState('');
  const [language, setLanguage] = useState<string>('');
  const [source, setSource] = useState('');

  const [analysis, setAnalysis] = useState<AnalyzeResponse | null>(null);
  const [extraction, setExtraction] = useState<AiAdExtraction | null>(null);
  const [finalType, setFinalType] = useState<DetectedType>('ROOM_WANTED_AD');
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('MATCH_OR_CREATE');
  const [ownerUserId, setOwnerUserId] = useState<number | null>(null);
  const [status, setStatus] = useState<'DRAFT' | 'PUBLISHED'>('DRAFT');
  const [lastCreated, setLastCreated] = useState<CreateResponse | null>(null);

  // Visibility & contact flags applied to every ad created in this run.
  // In-app messaging defaults OFF for the AI-create flow (synthetic users must
  // opt-in to chat explicitly) — mirrors the Roomaty importer.
  const [featured, setFeatured] = useState(false);
  const [showPhone, setShowPhone] = useState(true);
  const [showWhatsApp, setShowWhatsApp] = useState(true);
  const [showOwnerInfo, setShowOwnerInfo] = useState(false);
  const [showInAppMessaging, setShowInAppMessaging] = useState(false);
  const [hideLocation, setHideLocation] = useState(false);

  // Image upload (used after the ad is created — only meaningful for ROOM_AD,
  // which is the only ad type with an admin image endpoint today).
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageErrors, setImageErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
  const ALLOWED_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']);

  const addImages = useCallback((files: FileList | null) => {
    if (!files) return;
    const errs: string[] = [];
    const valid: File[] = [];
    Array.from(files).forEach((f) => {
      if (!ALLOWED_TYPES.has(f.type)) errs.push(t('aiAd.imagesErrorType') + `: ${f.name}`);
      else if (f.size > MAX_IMAGE_BYTES) errs.push(t('aiAd.imagesErrorSize') + `: ${f.name}`);
      else valid.push(f);
    });
    setImageErrors(errs);
    setImageFiles((prev) => [...prev, ...valid]);
  }, [t]);

  const removeImage = useCallback((idx: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const analyzeMutation = useMutation({
    mutationFn: () => aiAdApi.analyze(text, language || undefined, source || undefined),
    onSuccess: (data) => {
      setAnalysis(data);
      setExtraction(data.extraction);
      setFinalType(data.extraction.detectedType);
      setLastCreated(null);
      toast.success(t('aiAd.toast.analyzed', { engine: data.engine }));
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || t('aiAd.toast.analyzeFailed')),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!extraction) throw new Error('No extraction to save');
      const created = await aiAdApi.create({
        extraction,
        finalType,
        assignmentMode,
        ownerUserId: assignmentMode === 'ASSIGN_TO_USER' ? ownerUserId : null,
        status,
        featured,
        showPhone,
        showWhatsApp,
        showOwnerInfo,
        showInAppMessaging,
        hideLocation,
      });
      // Attach uploaded images to the newly created ad. Both ROOM_AD and
      // ROOM_WANTED_AD have admin image endpoints (multipart 'images' field).
      if (imageFiles.length > 0) {
        try {
          await aiAdApi.uploadAdImages(created.createdType, created.createdId, imageFiles);
        } catch (err: any) {
          toast.error(err?.response?.data?.message || t('aiAd.toast.imageUploadFailed'));
        }
      }
      return created;
    },
    onSuccess: (data) => {
      setLastCreated(data);
      toast.success(t('aiAd.toast.saved', { type: data.createdType, id: data.createdId }));
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || t('aiAd.toast.saveFailed')),
  });

  const ownerMissing = assignmentMode === 'ASSIGN_TO_USER' && !ownerUserId;
  const canSave = !!extraction && !ownerMissing && !createMutation.isPending;
  const phoneMissingForMatch =
    assignmentMode === 'MATCH_OR_CREATE' &&
    extraction !== null &&
    !extraction.phoneLocal;

  const patch = (partial: Partial<AiAdExtraction>) =>
    setExtraction((prev) => (prev ? { ...prev, ...partial } : prev));

  return (
    <div>
      <PageHeader
        title={t('aiAd.title')}
        subtitle={t('aiAd.subtitle')}
      />

      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span>ردود واتساب على إعلانات الذكاء الاصطناعي</span>
          <Link to="/ai-whatsapp-updates">
            <Button size="small">فتح صفحة الردود</Button>
          </Link>
        </Space>
      </Card>

      <Card title={t('aiAd.inputTitle')}>
        <div className="ds-form">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {SAMPLES.map((s) => (
              <Button
                key={s.label}
                size="small"
                type="text"
                onClick={() => setText(s.text)}
              >{s.label}</Button>
            ))}
          </div>
          <Input.TextArea
            value={text}
            onChange={(e: any) => setText(e.target.value)}
            placeholder={t('aiAd.textPlaceholder')}
            rows={6}
            showCount
            maxLength={6000}
            data-allstak-mask="true"
            data-sensitive="true"
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 12 }}>
            <div className="ds-form-item">
              <label className="ds-form-label">{t('aiAd.language')}</label>
              <Select
                value={language || undefined}
                onChange={(v: any) => setLanguage(v ?? '')}
                allowClear
                placeholder={t('aiAd.autoDetect')}
                options={[
                  { value: 'ar', label: 'العربية' },
                  { value: 'en', label: 'English' },
                ]}
              />
            </div>
            <div className="ds-form-item">
              <label className="ds-form-label">{t('aiAd.source')}</label>
              <Input
                value={source}
                onChange={(e: any) => setSource(e.target.value)}
                placeholder={t('aiAd.sourcePlaceholder')}
              />
            </div>
          </div>
          <Space>
            <Button
              type="primary"
              onClick={() => analyzeMutation.mutate()}
              loading={analyzeMutation.isPending}
              disabled={!text.trim()}
            >
              {t('aiAd.analyze')}
            </Button>
            <Button onClick={() => { setText(''); setAnalysis(null); setExtraction(null); setLastCreated(null); }} disabled={analyzeMutation.isPending}>
              {t('common.reset')}
            </Button>
            {analysis && (
              <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>
                {t('aiAd.engine')}: <Tag color={analysis.engine === 'openai' ? 'success' : 'warning'}>
                  {analysis.engine}{analysis.model ? ` · ${analysis.model}` : ''}
                </Tag>
              </span>
            )}
          </Space>
        </div>
      </Card>

      {extraction && (
        <>
          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'minmax(360px,1fr) minmax(360px,1fr)', gap: 16 }}>
            <Card title={t('aiAd.reviewTitle')}>
              <ReviewForm extraction={extraction} patch={patch} finalType={finalType} setFinalType={setFinalType} />
            </Card>
            <Card title={t('aiAd.previewTitle')}>
              <PreviewCard extraction={extraction} finalType={finalType} />
            </Card>
          </div>

          {(extraction.warnings?.length || extraction.missingFields?.length) ? (
            <div style={{ marginTop: 12 }}>
              <Alert
                type={extraction.missingFields?.length ? 'warning' : 'info'}
                showIcon
                message={t('aiAd.warningsTitle')}
                description={
                  <>
                    {extraction.missingFields?.length ? (
                      <div style={{ marginBottom: 6 }}>
                        <strong>{t('aiAd.missingFields')}:</strong>{' '}
                        {extraction.missingFields.map((f) => <Tag key={f} color="warning">{f}</Tag>)}
                      </div>
                    ) : null}
                    {extraction.warnings?.length ? (
                      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                        {extraction.warnings.map((w, i) => <li key={i} style={{ fontSize: 13 }}>{w}</li>)}
                      </ul>
                    ) : null}
                  </>
                }
              />
            </div>
          ) : null}

          <Card title={t('aiAd.saveTitle')} style={{ marginTop: 16 }}>
            <div className="ds-form">
              <div className="ds-form-item">
                <label className="ds-form-label required">{t('aiAd.assignmentMode')}</label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <ModeCard
                    active={assignmentMode === 'ASSIGN_TO_USER'}
                    title={t('aiAd.mode.assignTitle')}
                    description={t('aiAd.mode.assignDesc')}
                    onClick={() => setAssignmentMode('ASSIGN_TO_USER')}
                  />
                  <ModeCard
                    active={assignmentMode === 'MATCH_OR_CREATE'}
                    title={t('aiAd.mode.matchTitle')}
                    description={t('aiAd.mode.matchDesc')}
                    badge={t('aiAd.mode.recommended')}
                    onClick={() => setAssignmentMode('MATCH_OR_CREATE')}
                  />
                </div>
              </div>

              {assignmentMode === 'ASSIGN_TO_USER' && (
                <div className="ds-form-item">
                  <label className="ds-form-label required">{t('aiAd.ownerUser')}</label>
                  <UserSelect
                    value={ownerUserId}
                    onChange={setOwnerUserId}
                    placeholder={t('aiAd.ownerPlaceholder')}
                    status={ownerMissing ? 'error' : undefined}
                  />
                  {ownerMissing && <div className="ds-form-error">{t('aiAd.ownerRequired')}</div>}
                </div>
              )}

              {phoneMissingForMatch && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('aiAd.phoneMissingTitle')}
                  description={t('aiAd.phoneMissingDesc')}
                />
              )}

              <Divider style={{ margin: '4px 0' }} />

              {/* ── Visibility & Contact flags ── */}
              <div className="ds-form-item">
                <label className="ds-form-label">{t('aiAd.visibilityTitle')}</label>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
                  {t('aiAd.visibilityHint')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                  {([
                    { key: 'featured', label: t('aiAd.flagFeatured'), desc: t('aiAd.flagFeaturedDesc'), value: featured, set: setFeatured },
                    { key: 'showPhone', label: t('aiAd.flagShowPhone'), desc: t('aiAd.flagShowPhoneDesc'), value: showPhone, set: setShowPhone },
                    { key: 'showWhatsApp', label: t('aiAd.flagShowWhatsApp'), desc: t('aiAd.flagShowWhatsAppDesc'), value: showWhatsApp, set: setShowWhatsApp },
                    { key: 'showOwnerInfo', label: t('aiAd.flagShowOwnerInfo'), desc: t('aiAd.flagShowOwnerInfoDesc'), value: showOwnerInfo, set: setShowOwnerInfo },
                    { key: 'showInAppMessaging', label: t('aiAd.flagShowInAppMessaging'), desc: t('aiAd.flagShowInAppMessagingDesc'), value: showInAppMessaging, set: setShowInAppMessaging },
                    { key: 'hideLocation', label: t('aiAd.flagHideLocation'), desc: t('aiAd.flagHideLocationDesc'), value: hideLocation, set: setHideLocation },
                  ] as const).map(({ key, label, desc, value, set }) => (
                    <div
                      key={key}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: 12,
                        border: '1px solid var(--grey-200)',
                        borderRadius: 8,
                        background: 'var(--bg-card)',
                      }}
                    >
                      <Switch checked={value} onChange={(v: boolean) => (set as (v: boolean) => void)(v)} disabled={createMutation.isPending} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>{desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Image upload (attached to the created ad) ── */}
              <div className="ds-form-item">
                <label className="ds-form-label">{t('aiAd.imagesTitle')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    {t('aiAd.imagesHint')}
                  </div>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={createMutation.isPending}
                  >
                    {t('aiAd.imagesAdd')}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(ev) => { addImages(ev.target.files); ev.target.value = ''; }}
                  />
                  {imageErrors.map((err, i) => (
                    <div key={i} style={{ color: 'var(--error)', fontSize: 12 }}>{err}</div>
                  ))}
                  {imageFiles.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {imageFiles.map((f, i) => (
                        <div
                          key={i}
                          style={{
                            position: 'relative',
                            width: 80, height: 80,
                            border: '1px solid var(--grey-200)',
                            borderRadius: 6,
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={URL.createObjectURL(f)}
                            alt={f.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(i)}
                            style={{
                              position: 'absolute', top: 2, right: 2,
                              background: 'rgba(0,0,0,.55)', border: 'none',
                              borderRadius: 4, padding: '2px 4px', cursor: 'pointer',
                              color: '#fff', lineHeight: 1,
                            }}
                            title={t('aiAd.imagesRemove')}
                          >
                            <DeleteOutlined style={{ fontSize: 12 }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="ds-form-item" style={{ maxWidth: 280 }}>
                <label className="ds-form-label">{t('aiAd.status')}</label>
                <Select
                  value={status}
                  onChange={(v: any) => setStatus(v)}
                  options={[
                    { value: 'DRAFT', label: t('aiAd.statusDraft') },
                    { value: 'PUBLISHED', label: t('aiAd.statusPublished') },
                  ]}
                />
              </div>

              <Space>
                <Button type="primary" loading={createMutation.isPending} disabled={!canSave}
                        onClick={() => createMutation.mutate()}>
                  {finalType === 'ROOM_AD' ? t('aiAd.saveRoomAd') : t('aiAd.saveRoomWanted')}
                </Button>
                {lastCreated && (
                  <Button
                    onClick={() => {
                      const base = lastCreated.createdType === 'ROOM_AD' ? 'room-ads' : 'room-wanted';
                      navigate(`/${base}/${lastCreated.createdId}/edit`);
                    }}
                  >
                    {t('aiAd.openCreated', { id: lastCreated.createdId })}
                  </Button>
                )}
              </Space>

              {lastCreated && (
                <Alert
                  type="success"
                  showIcon
                  message={t('aiAd.savedTitle')}
                  description={
                    <div>
                      <Tag color="success">{lastCreated.createdType}</Tag>
                      <span style={{ marginInlineStart: 8 }}>
                        id #<strong>{lastCreated.createdId}</strong>,
                        &nbsp;{t('aiAd.user')}:{' '}
                        {lastCreated.assignedUserId ? (
                          <Link to={`/users/${lastCreated.assignedUserId}`}>#{lastCreated.assignedUserId}</Link>
                        ) : '—'}
                        &nbsp;({lastCreated.userAssignment})
                      </span>
                      {lastCreated.warnings?.length ? (
                        <ul style={{ margin: '8px 0 0', paddingInlineStart: 18 }}>
                          {lastCreated.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      ) : null}
                    </div>
                  }
                />
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/* ── Review form ───────────────────────────────────────────────────── */

function ReviewForm({
  extraction: e,
  patch,
  finalType,
  setFinalType,
}: {
  extraction: AiAdExtraction;
  patch: (p: Partial<AiAdExtraction>) => void;
  finalType: DetectedType;
  setFinalType: (t: DetectedType) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="ds-form">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 4 }}>
        <strong>{t('aiAd.detected')}:</strong>
        <Tag color={finalType === 'ROOM_WANTED_AD' ? 'purple' : 'info'}>{finalType}</Tag>
        <span style={{ color: 'var(--text-tertiary)' }}>
          ({t('aiAd.confidence')}: <Tag color={toneForConfidence(e.confidence)}>{(e.confidence ?? 0).toFixed(2)}</Tag>)
        </span>
        <div style={{ marginInlineStart: 'auto' }}>
          <Select
            value={finalType}
            onChange={(v: any) => setFinalType(v)}
            options={[
              { value: 'ROOM_WANTED_AD', label: t('aiAd.typeWanted') },
              { value: 'ROOM_AD', label: t('aiAd.typeAd') },
            ]}
          />
        </div>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 12 }}>
        <Field label={t('aiAd.field.title')}>
          <Input value={e.title ?? ''} onChange={(ev: any) => patch({ title: ev.target.value })} />
        </Field>
        <Field label={t('aiAd.field.personName')}>
          <Input value={e.personName ?? ''} onChange={(ev: any) => patch({ personName: ev.target.value })} data-allstak-mask="true" data-sensitive="true" />
        </Field>
        <Field label={t('aiAd.field.city')}>
          <CitySelect
            value={e.cityId ?? null}
            onChange={(id, label) => patch({ cityId: id, cityName: label ?? null, districtId: null, districtName: null })}
            placeholder={t('aiAd.field.city')}
          />
        </Field>
        <Field label={t('aiAd.field.district')}>
          <DistrictSelect
            cityId={e.cityId ?? null}
            value={e.districtId ?? null}
            onChange={(id, label) => patch({ districtId: id, districtName: label ?? null })}
            disabled={!e.cityId}
            placeholder={t('aiAd.field.district')}
          />
        </Field>
        <Field label={t('aiAd.field.phonePrefix')}>
          <Input value={e.phonePrefix ?? ''} onChange={(ev: any) => patch({ phonePrefix: ev.target.value })} autoComplete="tel-country-code" data-allstak-mask="true" data-sensitive="true" />
        </Field>
        <Field label={t('aiAd.field.phoneLocal')}>
          <Input value={e.phoneLocal ?? ''} onChange={(ev: any) => patch({ phoneLocal: ev.target.value })} autoComplete="tel-national" data-allstak-mask="true" data-sensitive="true" />
        </Field>
        <Field label={t('aiAd.field.countryCode')}>
          <Input value={e.phoneCountryCode ?? ''} onChange={(ev: any) => patch({ phoneCountryCode: ev.target.value })} />
        </Field>
        <Field label={t('aiAd.field.gender')}>
          <Select
            value={e.gender ?? 'UNKNOWN'}
            onChange={(v: any) => patch({ gender: v as Gender })}
            options={[
              { value: 'UNKNOWN', label: t('aiAd.unknown') },
              { value: 'MALE', label: t('aiAd.male') },
              { value: 'FEMALE', label: t('aiAd.female') },
            ]}
          />
        </Field>
        <Field label={t('aiAd.field.age')}>
          <InputNumber value={e.age ?? undefined} min={16} max={99} onChange={(v: any) => patch({ age: v ?? null })} style={{ width: '100%' }} />
        </Field>
        <Field label={t('aiAd.field.occupation')}>
          <Select
            value={e.occupation ?? undefined}
            onChange={(v: any) => patch({ occupation: v ?? null })}
            allowClear
            placeholder={t('aiAd.field.occupation')}
            options={[
              { value: 'STUDENT', label: t('aiAd.occupation.student') },
              { value: 'EMPLOYEE', label: t('aiAd.occupation.employee') },
              { value: 'NO_PREFERENCE', label: t('aiAd.occupation.noPreference') },
            ]}
          />
        </Field>
        <Field label={t('aiAd.field.nationality')}>
          <NationalitySelect
            value={e.nationalityId ?? null}
            onChange={(id, label) => patch({ nationalityId: id, nationality: label ?? null })}
            placeholder={t('aiAd.field.nationality')}
          />
        </Field>
        <Field label={t('aiAd.field.smoking')}>
          <Select
            value={e.smokingPreference ?? 'UNKNOWN'}
            onChange={(v: any) => patch({ smokingPreference: v as Smoking })}
            options={[
              { value: 'UNKNOWN', label: t('aiAd.unknown') },
              { value: 'SMOKER', label: t('aiAd.smoker') },
              { value: 'NON_SMOKER', label: t('aiAd.nonSmoker') },
            ]}
          />
        </Field>
        {finalType === 'ROOM_AD' ? (
          <>
            <Field label={t('aiAd.field.price')}>
              <InputNumber value={e.price ?? undefined} min={0} onChange={(v: any) => patch({ price: v ?? null })} style={{ width: '100%' }} />
            </Field>
            <Field label={t('aiAd.field.propertyType')}>
              <Select
                value={e.propertyType ?? undefined}
                onChange={(v: any) => patch({ propertyType: v ?? null })}
                allowClear
                placeholder={t('aiAd.field.propertyType')}
                options={[
                  { value: 'APARTMENT', label: t('aiAd.propertyType.apartment') },
                  { value: 'VILLA', label: t('aiAd.propertyType.villa') },
                ]}
              />
            </Field>
            <Field label={t('aiAd.field.roomType')}>
              <Select
                value={e.roomType ?? undefined}
                onChange={(v: any) => patch({ roomType: v ?? null })}
                allowClear
                placeholder={t('aiAd.field.roomType')}
                options={[
                  { value: 'PRIVATE_BATHROOM', label: t('aiAd.roomType.private') },
                  { value: 'SHARED_BATHROOM', label: t('aiAd.roomType.shared') },
                  { value: 'BOTH', label: t('aiAd.roomType.both') },
                ]}
              />
            </Field>
          </>
        ) : (
          <>
            <Field label={t('aiAd.field.budgetMin')}>
              <InputNumber value={e.budgetMin ?? undefined} min={0} onChange={(v: any) => patch({ budgetMin: v ?? null })} style={{ width: '100%' }} />
            </Field>
            <Field label={t('aiAd.field.budgetMax')}>
              <InputNumber value={e.budgetMax ?? undefined} min={0} onChange={(v: any) => patch({ budgetMax: v ?? null })} style={{ width: '100%' }} />
            </Field>
          </>
        )}
      </div>

      <div className="ds-form-item">
        <label className="ds-form-label">{t('aiAd.field.description')}</label>
        <Input.TextArea
          value={e.description ?? ''}
          onChange={(ev: any) => patch({ description: ev.target.value })}
          rows={4}
          data-allstak-mask="true"
          data-sensitive="true"
        />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="ds-form-item">
      <label className="ds-form-label">{label}</label>
      {children}
    </div>
  );
}

/* ── Preview card ─────────────────────────────────────────────────── */

function PreviewCard({ extraction: e, finalType }: { extraction: AiAdExtraction; finalType: DetectedType }) {
  const { t } = useTranslation();
  const phoneDisplay = e.phonePrefix && e.phoneLocal
    ? `${e.phonePrefix}${e.phoneLocal}`
    : (e.phoneNumber ?? '—');
  if (finalType === 'ROOM_WANTED_AD') {
    return (
      <div style={{ padding: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>
          {e.personName || t('aiAd.anonymous')}
          {e.age ? <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}> · {e.age}</span> : null}
        </h3>
        <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 10 }}>
          {[e.gender, e.nationality, e.cityName].filter(Boolean).join(' · ') || '—'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {e.occupation && <Tag color="info">{e.occupation}</Tag>}
          {e.smokingPreference && e.smokingPreference !== 'UNKNOWN' && <Tag color="warning">{e.smokingPreference}</Tag>}
          {e.preferredRoommateGender && e.preferredRoommateGender !== 'UNKNOWN' && <Tag>Roommate: {e.preferredRoommateGender}</Tag>}
          {(e.lifestyleTraits ?? []).map((x) => <Tag key={x}>{x}</Tag>)}
        </div>
        {e.description && (
          <p style={{ color: 'var(--text-primary)', margin: '6px 0', whiteSpace: 'pre-wrap', fontSize: 14 }}>
            {e.description}
          </p>
        )}
        <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
          <div>
            {t('aiAd.preview.budget')}:{' '}
            {isNegotiablePrice(e.budgetMin) && isNegotiablePrice(e.budgetMax)
              ? negotiableLabel(t)
              : `${isNegotiablePrice(e.budgetMin) ? '?' : e.budgetMin} – ${isNegotiablePrice(e.budgetMax) ? '?' : e.budgetMax} ${e.currency || 'SAR'}`}
          </div>
          <div>{t('aiAd.preview.phone')}: {phoneDisplay} {e.hasWhatsapp ? '· WhatsApp' : ''}</div>
        </div>
      </div>
    );
  }

  // ROOM_AD preview
  return (
    <div style={{ padding: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>
        {e.title || t('aiAd.untitled')}
      </h3>
      <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 10 }}>
        {[e.cityName, e.districtName].filter(Boolean).join(' · ') || '—'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <Tag color="primary">{isNegotiablePrice(e.price) ? negotiableLabel(t) : `${e.price} ${e.currency || 'SAR'}`}</Tag>
        {e.propertyType && <Tag color="info">{e.propertyType}</Tag>}
        {e.roomType && <Tag color="info">{e.roomType}</Tag>}
      </div>
      {e.description && (
        <p style={{ color: 'var(--text-primary)', margin: '6px 0', whiteSpace: 'pre-wrap', fontSize: 14 }}>
          {e.description}
        </p>
      )}
      <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
        <div>{t('aiAd.preview.contact')}: {phoneDisplay} {e.hasWhatsapp ? '· WhatsApp' : ''}</div>
      </div>
    </div>
  );
}

/* ── Mode card (reused visual from Roomaty import) ─────────────────── */

function ModeCard({ active, title, description, badge, onClick }: { active: boolean; title: string; description: string; badge?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: '1 1 300px',
        textAlign: 'start',
        padding: 16,
        borderRadius: 'var(--r-xl)',
        border: active ? '2px solid var(--color-primary)' : '1px solid var(--grey-200)',
        background: active ? 'var(--color-primary-soft)' : 'var(--bg-card)',
        boxShadow: active ? '0 0 0 4px rgba(255,179,102,.12)' : 'var(--shadow-xs)',
        cursor: 'pointer',
        transition: 'all .15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
        {badge && <Tag color="primary">{badge}</Tag>}
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 }}>{description}</div>
    </button>
  );
}
